import type { GraphData, GraphNode, EdgeType } from "../types/graph";
import { api, projectScope } from "./client";

// Raw shapes the backend actually returns (different from the frontend GraphData contract)
interface InfraApiResponse {
  resources: Array<{
    id: string; kind: string; name: string; namespace?: string | null;
    file_path: string; base_image?: string | null;
    labels?: Record<string, string> | null;
  }>;
  edges: Array<{ source_id: string; target_id: string; edge_type: string }>;
}

interface CrossServiceApiResponse {
  edges: Array<{
    source_id: string; source_name: string; source_file_path: string;
    target_id: string; target_name: string; target_file_path: string;
    edge_type: string; confidence?: number | null;
  }>;
  routes: Array<{
    id: string; method: string; path: string; handler: string; file_path?: string | null;
  }>;
}

export const graphApi = {
  getFullGraph(repoId: string, projectId?: string): Promise<GraphData> {
    return api.get<GraphData>(projectScope(`/repos/${repoId}/graph`, projectId));
  },

  getNeighbors(repoId: string, nodeId: string, hops: number, projectId?: string): Promise<GraphData> {
    return api.get<GraphData>(
      projectScope(`/repos/${repoId}/graph/neighbors/${nodeId}?hops=${hops}`, projectId)
    );
  },

  getCrossServiceGraph(repoId: string, projectId?: string): Promise<GraphData> {
    return api
      .get<CrossServiceApiResponse>(projectScope(`/repos/${repoId}/graph/cross-service`, projectId))
      .then((res) => {
        // Build a node per unique source/target endpoint seen in edges
        const nodeMap = new Map<string, GraphNode>();
        for (const e of res.edges) {
          if (!nodeMap.has(e.source_id)) {
            nodeMap.set(e.source_id, {
              id: e.source_id, name: e.source_name, fq_name: e.source_name,
              kind: "Function", file_path: e.source_file_path,
              start_line: null, end_line: null, language: null,
              community_id: null, fan_in: 0, fan_out: 0,
            });
          }
          if (!nodeMap.has(e.target_id)) {
            nodeMap.set(e.target_id, {
              id: e.target_id, name: e.target_name, fq_name: e.target_name,
              kind: "Function", file_path: e.target_file_path,
              start_line: null, end_line: null, language: null,
              community_id: null, fan_in: 0, fan_out: 0,
            });
          }
        }
        // Add Route nodes (these show up in the routes side-panel)
        for (const r of res.routes) {
          const label = `${r.method} ${r.path}`;
          nodeMap.set(r.id, {
            id: r.id, name: label, fq_name: r.handler || label,
            kind: "Route", file_path: r.file_path ?? "",
            start_line: null, end_line: null, language: null,
            community_id: null, fan_in: 0, fan_out: 0,
          });
        }
        return {
          nodes: [...nodeMap.values()],
          edges: res.edges.map((e, i) => ({
            id: `cs-${i}`,
            source: e.source_id,
            target: e.target_id,
            type: e.edge_type as EdgeType,
            confidence: e.confidence ?? undefined,
          })),
        };
      });
  },

  getInfrastructureGraph(repoId: string, projectId?: string): Promise<GraphData> {
    return api
      .get<InfraApiResponse>(projectScope(`/repos/${repoId}/graph/infrastructure`, projectId))
      .then((res) => ({
        // Map Resource nodes; keep extra fields for InfrastructureNode detection in the UI
        nodes: res.resources.map((r) => ({
          id: r.id,
          name: r.name,
          fq_name: r.name,
          kind: "Resource" as const,
          file_path: r.file_path,
          start_line: null,
          end_line: null,
          language: null,
          community_id: null,
          fan_in: 0,
          fan_out: 0,
          // InfrastructureNode extra fields (detected via `"resource_kind" in node`)
          resource_kind: r.kind,
          namespace: r.namespace ?? undefined,
          base_image: r.base_image ?? undefined,
          labels: r.labels ?? undefined,
        })) as GraphNode[],
        edges: res.edges.map((e, i) => ({
          id: `infra-${i}`,
          source: e.source_id,
          target: e.target_id,
          type: e.edge_type as EdgeType,
        })),
      }));
  },
};
